"use client";

import Image from "next/image";
import Link from "next/link";
import { useState } from "react";
import Button from "@/components/common/button";
import SectionEyebrow from "@/components/common/sectionEyebrow";
import FAIcon from "@/components/icons/faIcon";
import NewsGridCard from "@/components/news/newsGridCard";

const INITIAL_VISIBLE_STORIES = 12;
const STORIES_PER_PAGE = 12;

function FilterButton({ label, isActive, onClick, compact = false }) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={isActive}
      onClick={onClick}
      className={`cursor-pointer font-semibold uppercase tracking-[0.18em] transition-colors duration-200 ${
        compact
          ? "rounded-xl px-3 py-2 text-[0.65rem]"
          : "rounded-2xl px-5 py-3 text-[0.72rem]"
      } ${
        isActive
          ? "bg-primary text-[#06040c]"
          : "bg-[#f3f1ec] text-zinc-500 hover:bg-[#ece8df] hover:text-[#06040c]"
      }`}
    >
      {label}
    </button>
  );
}

function ArrowMark({ className = "" }) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 96 96"
      className={className}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M16 48H72M48 24L72 48L48 72"
        stroke="currentColor"
        strokeWidth="8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function TagBadge({ label, light = false }) {
  return (
    <span
      className={`rounded-md px-2 py-1 text-[0.65rem] font-medium ${
        light ? "bg-white/12 text-white/82" : "bg-[#f3f1ec] text-zinc-500"
      }`}
    >
      {label}
    </span>
  );
}

function ArticleMeta({ item, light = false }) {
  return (
    <div
      className={`flex flex-wrap items-center gap-3 ${
        light ? "text-white/76" : "text-[#06040c]/54"
      }`}
    >
      {item.tags.map((tag) => (
        <TagBadge key={`${item.id}-${tag}`} label={tag} light={light} />
      ))}
      <p
        className={`font-raleway text-sm tracking-tight leading-none sm:text-sm ${
          light ? "text-white/76" : "text-[#06040c]/54"
        }`}
      >
        {item.date}
      </p>
    </div>
  );
}

function RecentCompactCard({ item }) {
  return (
    <Link
      href={`/news/${item.id}`}
      className="group flex min-w-0 items-center gap-3 rounded-lg border border-[#06040c]/8 bg-white px-3 py-3 shadow-[0_12px_30px_rgba(6,4,12,0.04)] transition-colors duration-300 hover:bg-[#fbfaf7] focus-visible:bg-[#fbfaf7] focus-visible:outline-none"
    >
      <div className="relative h-14 w-19 shrink-0 overflow-hidden rounded-md bg-[#101416]">
        <div className={`absolute inset-0 ${item.frameClassName}`} />
        <Image
          src={item.image}
          alt={item.title}
          fill
          className="object-cover transition-transform duration-500 group-hover:scale-[1.04] "
          style={{ objectPosition: item.imagePosition }}
          sizes="76px"
        />
      </div>

      <div className="min-w-0 space-y-1.5">
        <p className="truncate font-raleway text-[1rem] font-semibold leading-tight tracking-[-0.03em] text-[#06040c]">
          {item.title}
        </p>
        <p className="font-raleway text-[0.72rem] font-semibold uppercase tracking-[0.16em] text-[#06040c]/42">
          {item.date}
        </p>
      </div>
    </Link>
  );
}

function RecentLeadCard({ item }) {
  return (
    <Link
      href={`/news/${item.id}`}
      className="group block h-full focus-visible:outline-none"
    >
      <article className="relative h-full min-h-112 overflow-hidden rounded-lg bg-[#101416] lg:min-h-120">
        <div className={`absolute inset-0 ${item.frameClassName}`} />
        <Image
          src={item.image}
          alt={item.title}
          fill
          className="object-cover transition-transform duration-700 brightness-90 grayscale-30 contrast-120 group-hover:scale-[1.03] group-focus-visible:scale-[1.03]"
          style={{ objectPosition: item.imagePosition }}
          sizes="(min-width: 1280px) 30vw, (min-width: 768px) 50vw, 100vw"
          priority
        />
        <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(6,7,10,0.04),rgba(6,7,10,0.18)_34%,rgba(6,7,10,0.9)_100%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(255,255,255,0.16),transparent_26%),radial-gradient(circle_at_bottom_left,rgba(0,0,0,0.18),transparent_30%)]" />

        <div className="relative flex h-full min-h-64 flex-col justify-end p-6 sm:p-7">
          <div className="space-y-4">
            <ArticleMeta item={item} light />

            <div className="space-y-4">
              <h3 className="max-w-md font-raleway text-[2rem] font-semibold leading-[0.98] tracking-[-0.06em] text-white sm:text-[2.45rem]">
                {item.title}
              </h3>
              {}
            </div>

            {}
          </div>
        </div>
      </article>
    </Link>
  );
}

function RecentImageCard({ item, className = "", titleClassName = "" }) {
  return (
    <Link
      href={`/news/${item.id}`}
      className={`group relative block overflow-hidden rounded-lg bg-[#101416] focus-visible:outline-none brightness-90 grayscale-30 contrast-120 ${className}`}
    >
      <div className={`absolute inset-0 ${item.frameClassName}`} />
      <Image
        src={item.image}
        alt={item.title}
        fill
        className="object-cover transition-transform duration-700 group-hover:scale-[1.03] group-focus-visible:scale-[1.03]"
        style={{ objectPosition: item.imagePosition }}
        sizes="(min-width: 1280px) 30vw, (min-width: 768px) 50vw, 100vw"
      />
      <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(6,7,10,0.04),rgba(6,7,10,0.16)_38%,rgba(6,7,10,0.84)_100%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(255,255,255,0.16),transparent_28%),radial-gradient(circle_at_bottom_left,rgba(0,0,0,0.18),transparent_30%)]" />

      <div className="relative flex h-full min-h-64 flex-col justify-end p-5 sm:p-6">
        <div className="space-y-3">
          <ArticleMeta item={item} light />
          <h3
            className={`max-w-md font-raleway text-[1.7rem] font-semibold leading-[1.02] tracking-[-0.05em] text-white sm:text-[1.95rem] ${titleClassName}`}
          >
            {item.title}
          </h3>
        </div>
      </div>
    </Link>
  );
}

function RecentCtaCard() {
  return (
    <article className="relative overflow-hidden rounded-lg bg-primary text-[#06040c] min-h-64">
      <ArrowMark className="absolute right-2 bottom-2 h-18 w-18 text-[#06040c]/10 sm:h-22 sm:w-22" />

      <div className="relative flex h-full min-h-64 flex-col justify-between p-5 sm:p-6">
        <div className="space-y-3">
          <h3 className="max-w-xs font-raleway text-[1.7rem] font-semibold leading-[1.02] tracking-[-0.05em] text-[#06040c] sm:text-[1.95rem]">
            Need practical advice behind the headline?
          </h3>
        </div>

        <Button
          href="/contact#contact-form"
          text="Speak with us"
          variant="dark"
          endIcon={
            <span className="flex min-h-9 min-w-9 items-center justify-center rounded-full  bg-white text-[#06040c]">
              <FAIcon icon="phone" size={12} className="m-0" noMargin />
            </span>
          }
        />
      </div>
    </article>
  );
}

export default function NewsArchive({ newsItems, newsCategories }) {
  const sortedNewsItems = [...newsItems].sort(
    (left, right) =>
      new Date(right.publishedAt ?? right.date) -
      new Date(left.publishedAt ?? left.date),
  );
  const [activeCategory, setActiveCategory] = useState(
    newsCategories[0] ?? "All",
  );
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE_STORIES);
  const selectedCategory = newsCategories.includes(activeCategory)
    ? activeCategory
    : newsCategories[0] ?? "All";
  const recentItems = sortedNewsItems.slice(0, 4);
  const leadItem = recentItems[0];
  const primaryImageItem = recentItems[1];
  const stackedLeadItem = recentItems[2];

  const filteredItems =
    selectedCategory === "All"
      ? sortedNewsItems
      : sortedNewsItems.filter((item) => item.tags.includes(selectedCategory));
  const visibleItems = filteredItems.slice(0, visibleCount);
  const hasMoreStories = visibleItems.length < filteredItems.length;

  const selectCategory = (category) => {
    setActiveCategory(category);
    setFiltersOpen(false);
    setVisibleCount(INITIAL_VISIBLE_STORIES);
  };

  return (
    <section className="mx-auto flex w-full max-w-328 flex-col gap-12 lg:gap-14">
      <section className="space-y-8 lg:space-y-10">
        <div className="grid gap-5 lg:grid-cols-2 lg:items-stretch">
          {}

          {primaryImageItem ? (
            <RecentImageCard
              item={primaryImageItem}
              className="min-h-112 lg:min-h-160"
              titleClassName="text-[2rem] sm:text-[2.3rem]"
            />
          ) : null}

          <div className="grid gap-5">
            {stackedLeadItem ? (
              <RecentImageCard
                key={`stacked-${stackedLeadItem.id}`}
                item={stackedLeadItem}
              />
            ) : null}
            <RecentCtaCard />
          </div>
        </div>
      </section>

      <div className="grid gap-8 border-t border-[#ece7dd] pt-10  lg:gap-10">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between lg:text-right mt-10">
          {newsCategories.length > 1 ? (
            <>
              <div className="lg:hidden">
                <button
                  type="button"
                  aria-expanded={filtersOpen}
                  aria-controls="news-category-filters"
                  onClick={() => setFiltersOpen((isOpen) => !isOpen)}
                  className="flex w-full min-w-0 items-center justify-between gap-3 rounded-xl border border-[#06040c]/10 bg-white/70 px-4 py-3 text-left"
                >
                  <span className="text-[0.68rem] font-semibold uppercase tracking-[0.18em] text-[#06040c]/55">
                    Filter by topic
                  </span>
                  <span className="flex min-w-0 items-center gap-2">
                    <span className="truncate text-sm font-semibold text-[#06040c]">
                      {selectedCategory}
                    </span>
                    <FAIcon
                      icon="chevron-down"
                      size={12}
                      noMargin
                      className={`shrink-0 transition-transform duration-200 ${
                        filtersOpen ? "rotate-180" : ""
                      }`}
                    />
                  </span>
                </button>

                {filtersOpen ? (
                  <div
                    id="news-category-filters"
                    role="tablist"
                    aria-label="News categories"
                    className="mt-3 flex flex-wrap gap-2"
                  >
                    {newsCategories.map((category) => (
                      <FilterButton
                        key={category}
                        label={category}
                        compact
                        isActive={selectedCategory === category}
                        onClick={() => selectCategory(category)}
                      />
                    ))}
                  </div>
                ) : null}
              </div>

              <div className="hidden lg:block">
                <div
                  role="tablist"
                  aria-label="News categories"
                  className="flex flex-wrap gap-3"
                >
                  {newsCategories.map((category) => (
                    <FilterButton
                      key={category}
                      label={category}
                      isActive={selectedCategory === category}
                      onClick={() => selectCategory(category)}
                    />
                  ))}
                </div>
              </div>
            </>
          ) : (
            <div />
          )}

          <p className="font-raleway text-sm uppercase tracking-[0.16em] text-[#06040c]/42">
            Showing {filteredItems.length} article
            {filteredItems.length === 1 ? "" : "s"}
          </p>
        </div>
      </div>

      <section id="news-archive" className="space-y-8 ">
        <section className="grid gap-6 md:grid-cols-2 xl:grid-cols-4">
          {visibleItems.map((item) => (
            <NewsGridCard key={item.slug ?? item.id} item={item} />
          ))}
        </section>

        {hasMoreStories ? (
          <div className="flex justify-center pt-2">
            <Button
              text="More stories"
              variant="dark"
              onClick={() =>
                setVisibleCount(
                  (currentCount) => currentCount + STORIES_PER_PAGE,
                )
              }
              endIcon={
                <FAIcon icon="arrow-down" size={12} className="m-0" noMargin />
              }
            />
          </div>
        ) : null}
      </section>
    </section>
  );
}
